import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSkillCollectionDetailQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface RestoreSkillCollectionVersionParams {
  workspaceId: number;
  collectionId: string;
  versionId: string;
}

interface RestoreSkillCollectionVersionResponse {
  success: boolean;
  version: {
    id: string;
    number: number;
    created_at: string;
  };
}

export const useRestoreSkillCollectionVersion = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      collectionId,
      versionId,
    }: RestoreSkillCollectionVersionParams): Promise<RestoreSkillCollectionVersionResponse> => {
      const res = await fetch(
        ENDPOINTS.skill_collections.version_restore(workspaceId, collectionId, versionId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
        },
      );
      return handleResponse(res);
    },
    onSuccess: async (_, variables) => {
      await invalidateSkillCollectionDetailQueries(queryClient, variables);
      displayToast("Version restored", ToastType.success);
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
